import { Box, Button, Paper, Stack } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { createQuickBooksInvoice } from '@sophocles/api/quickbooks';
import {
  editInvoice,
  loadInvoice,
  markInvoicesCompleteByIds,
  pushQuickBooksInvoice,
} from '@sophocles/features/billing/api';
import { selectInvoice } from '@sophocles/features/billing/slice';
import { selectQuickBooksCompany } from '@sophocles/features/quickbooks/slice';
import quickBooksIconUrl from '@sophocles/public/qbo-icon.png';
import { BillingTitleBar, InvoiceDetailTable } from '@sophocles/views/billing/components';

function InvoiceDetailView(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const invoice = useSelector(selectInvoice(props.match.params.invoiceId));
  const quickBooksCompany = useSelector(selectQuickBooksCompany);

  const isQuickBooksSignedIn = quickBooksCompany != null;

  const [isEditMode, setIsEditMode] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [invoiceData, setInvoiceData] = useState({});
  const [workingData, setWorkingData] = useState([]);

  const { invoiceId } = props.match.params;

  const parseAndAssignInvoiceData = invoice => {
    const categories = {};
    invoice.items.forEach((item, invoiceItemIndex) => {
      const itemData = {
        id: item.id,
        index: invoiceItemIndex,
        name: item.description,
        quantity: parseFloat(item.quantity),
        price: parseFloat(item.price),
        billed_entities_count: item.billed_entities_count,
        billed_entities_breakdown: item.billed_entities_breakdown,
      };
      if (item.category?.name) {
        if (item.category.name in categories) {
          categories[item.category.name].items.push(itemData);
        } else {
          categories[item.category.name] = { id: item.category.id, type_id: item.category_type, items: [itemData] };
        }
      } else {
        if (categories['Uncategorized'] == null) {
          categories['Uncategorized'] = { id: null, type_id: null, items: [] };
        }
        categories['Uncategorized'].items.push(itemData);
      }
    });

    const transformedData = Object.entries(categories).map(([categoryName, category]) => {
      return {
        id: category.id,
        type_id: category.type_id,
        name: categoryName,
        items: category.items,
      };
    });

    setInvoiceData(invoice);
    setWorkingData(transformedData);
  };

  const handleCancel = () => {
    parseAndAssignInvoiceData(invoice);
  };

  const handleSave = () => {
    setIsBusy(true);
    dispatch(editInvoice({ id: invoice.id, items: invoiceData.items })).finally(() => {
      setIsBusy(false);
      setIsEditMode(false);
    });
  };

  useEffect(() => {
    dispatch(loadInvoice(invoiceId))
      .unwrap()
      .then(invoice => {
        if (invoice != null) {
          parseAndAssignInvoiceData(invoice);
        }
      });
  }, []);

  useEffect(() => {
    const newInvoiceData = { ...invoice };
    const items = [];
    workingData.forEach(category => {
      const categoryId = category.id;
      category.items.forEach(item => {
        const itemId = item.id ?? null;
        const { quantity, price, name } = item;
        if (itemId == null) {
          items.push({
            category_id: categoryId,
            category_type: category.type_id,
            description: name,
            price: price,
            quantity: quantity,
            invoice: invoice.id,
          });
        } else {
          items.push({
            id: itemId,
            category_id: categoryId,
            category_type: category.type_id,
            description: name,
            price: price,
            quantity: quantity,
            invoice: invoice.id,
          });
        }
      });
    });
    newInvoiceData.items = items;
    setInvoiceData(newInvoiceData);
  }, [workingData]);

  let title = t('billing.invoiceDetail', 'Invoice Detail');
  if (invoice != null) {
    title = `${invoice.name} - ${invoice.start_date} - ${invoice.end_date} (${invoice.id})`;
  }

  return (
    <>
      <BillingTitleBar showBackButton title={title} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            disabled={isBusy}
            variant="contained"
            color={isEditMode ? 'secondary' : 'primary'}
            onClick={() => {
              if (isEditMode) {
                handleCancel();
              }
              setIsEditMode(!isEditMode);
            }}
          >
            {isEditMode ? t('billing.cancel', 'Cancel') : t('billing.edit', 'Edit')}
          </Button>
          {isEditMode && (
            <Button disabled={isBusy} variant="contained" color="success" onClick={handleSave}>
              {t('billing.save', 'Save')}
            </Button>
          )}

          {invoice?.status == 'ready' && (
            <Button
              color="success"
              variant="contained"
              onClick={() => {
                dispatch(markInvoicesCompleteByIds([invoice.id])).unwrap();
              }}
            >
              {t('billing.markComplete', 'Mark Complete')}
            </Button>
          )}
          {isQuickBooksSignedIn && (
            <Fragment>
              {invoice?.foreign_id == null ? (
                <Button
                  variant="contained"
                  onClick={() => {
                    dispatch(pushQuickBooksInvoice(invoice.id)).unwrap();
                  }}
                >
                  {t('billing.pushInvoice', 'Push Invoice')}
                </Button>
              ) : (
                <a href={`https://app.qbo.intuit.com/app/invoice?txnId=${invoice.foreign_id}`}>
                  <img
                    src={quickBooksIconUrl}
                    style={{ maxHeight: '24px' }}
                    alt={t('billing.quickbooks', 'Quickbooks')}
                  />
                </a>
              )}
            </Fragment>
          )}
        </Stack>

        <Paper sx={{ display: 'flex', flex: 1 }}>
          <InvoiceDetailTable
            editable={isEditMode}
            invoiceData={workingData}
            setInvoiceData={setWorkingData}
            categorySubtotals={invoice?.category_subtotals}
            serverTotal={invoice?.total}
            invoiceId={invoice?.id ?? null}
          />
        </Paper>
      </Box>
    </>
  );
}

export default InvoiceDetailView;
